import React, { useState, useEffect } from 'react';
import { FaTimes } from 'react-icons/fa';
import { adminCategoryAPI } from '../services/api';
import '../styles/Modal.css';

const CategoryModal = ({ mode, category, onClose, onSaved }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    imageUrl: '',
    color: '#667eea'
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState('');

  useEffect(() => {
    if (mode === 'edit' && category) {
      setFormData({
        title: category.title || '',
        description: category.description || '',
        imageUrl: category.imageUrl || '',
        color: category.color || '#667eea'
      });
    }
  }, [mode, category]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Category title is required';
    } else if (formData.title.length < 2) {
      newErrors.title = 'Category title must be at least 2 characters';
    } else if (formData.title.length > 100) {
      newErrors.title = 'Category title cannot exceed 100 characters';
    }

    if (formData.description && formData.description.length > 1000) {
      newErrors.description = 'Description cannot exceed 1000 characters';
    }

    if (formData.imageUrl && formData.imageUrl.length > 500) {
      newErrors.imageUrl = 'Image URL cannot exceed 500 characters';
    }

    if (formData.imageUrl) {
      try {
        new URL(formData.imageUrl.startsWith('http') ? formData.imageUrl : `https://${formData.imageUrl}`);
      } catch {
        newErrors.imageUrl = 'Please provide a valid URL';
      }
    }

    if (formData.color) {
      const hexPattern = /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/;
      const colorNamePattern = /^[a-zA-Z]+$/;
      if (!hexPattern.test(formData.color) && !colorNamePattern.test(formData.color)) {
        newErrors.color = 'Color must be a valid hex code (e.g., #FF5733) or color name';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    setServerError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      let response;
      if (mode === 'create') {
        response = await adminCategoryAPI.create(formData);
      } else {
        response = await adminCategoryAPI.update(category._id, formData);
      }

      if (response.data.success) {
        onSaved();
      } else {
        setServerError(response.data.message || 'Operation failed');
      }
    } catch (error) {
      console.error('Category save error:', error);
      const message = error.response?.data?.message || 'An error occurred';
      setServerError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal">
        <div className="modal-header">
          <h2>{mode === 'create' ? 'Add New Category' : 'Edit Category'}</h2>
          <button className="close-btn" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-body">
          {serverError && (
            <div className="error-alert">{serverError}</div>
          )}

          <div className="form-group">
            <label htmlFor="title">Category Title *</label>
            <input
              type="text"
              id="title"
              name="title"
              value={formData.title}
              onChange={handleChange}
              className={errors.title ? 'error' : ''}
              disabled={loading}
              placeholder="Enter category title"
            />
            {errors.title && <span className="error-message">{errors.title}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="description">Description</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={errors.description ? 'error' : ''}
              disabled={loading}
              placeholder="Enter category description (optional)"
              rows="4"
            />
            {errors.description && <span className="error-message">{errors.description}</span>}
            <small className="character-count">
              {formData.description.length}/1000 characters
            </small>
          </div>

          <div className="form-group">
            <label htmlFor="imageUrl">Image URL</label>
            <input
              type="text"
              id="imageUrl"
              name="imageUrl"
              value={formData.imageUrl}
              onChange={handleChange}
              className={errors.imageUrl ? 'error' : ''}
              disabled={loading}
              placeholder="https://example.com/image.jpg"
            />
            {errors.imageUrl && <span className="error-message">{errors.imageUrl}</span>}
            <small className="help-text">
              Enter a full URL to an image (optional)
            </small>
          </div>

          {/* <div className="form-group">
            <label htmlFor="color">Color</label>
            <div className="color-input-group">
              <input
                type="color"
                id="colorPicker"
                value={formData.color.startsWith('#') ? formData.color : '#667eea'}
                onChange={(e) => setFormData(prev => ({ ...prev, color: e.target.value }))}
                disabled={loading}
                className="color-picker"
              />
              <input
                type="text"
                id="color"
                name="color"
                value={formData.color}
                onChange={handleChange}
                className={errors.color ? 'error' : ''}
                disabled={loading}
                placeholder="#667eea or blue"
              />
            </div>
            {errors.color && <span className="error-message">{errors.color}</span>}
            <small className="help-text">
              Enter hex code (e.g., #FF5733) or color name (e.g., blue)
            </small>
          </div> */}

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Saving...' : mode === 'create' ? 'Create Category' : 'Update Category'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryModal;
